import { dateFormat } from '@constants/dateFormat'
import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'
import moment from 'moment/moment'

export const usePayroll = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  const usePayrollOverviewQuery = (filter) =>
    useQuery({
      queryKey: ['payrollOverviewQuery', filter],
      queryFn: ({ queryKey }) => api.post('payroll/overview', { filter: queryKey[1] }),
      refetchOnWindowFocus: false,
      enabled: !!filter,
    })

  const usePayrollExpandQuery = (startDate, driverId) =>
    useQuery({
      queryKey: ['payrollExpandQuery', startDate, driverId],
      queryFn: ({ queryKey }) => {
        return api.post('payroll/overview_detail', undefined, {
          queryParams: new URLSearchParams({ date: startDate, driver_id: queryKey[2] }),
        })
      },
    })

  const usePayrollDatesQuery = () =>
    useQuery({
      queryKey: ['payrollDatesQuery'],
      queryFn: ({ queryKey }) =>
        api.post('payroll/dates', undefined, { queryParams: `date=${moment.utc().toISOString()}` }),

      refetchOnWindowFocus: false,
    })

  const usePayPeriodDetailsQuery = (filters, driverId) =>
    useQuery({
      queryKey: ['payPeriodDetailsQuery', filters, driverId],
      queryFn: ({ queryKey }) => api.post('payroll/pay_period', { filter: { ...filters, driver_id: driverId } }),
      refetchOnWindowFocus: false,
    })

  const useSpecialPayRequestsQuery = (filter, dateRangeFilter) =>
    useQuery({
      queryKey: ['specialPayRequestsQuery', filter, dateRangeFilter],
      queryFn: ({ queryKey }) =>
        api.post('payroll/special_pay_request/overview', {
          filter: queryKey[1],
          date_to: dateRangeFilter?.To.utc().toISOString(),
          date_from: dateRangeFilter?.From.utc().toISOString(),
        }),
    })

  const useSpecialPayTypesQuery = () =>
    useQuery({ queryKey: ['specialPayTypesQuery'], queryFn: ({ queryKey }) => api.post('payroll/special_pay/list') })

  const useSpecialPayOverridesQuery = () =>
    useQuery({
      queryKey: ['specialPayOverridesQuery'],
      queryFn: () => api.post('payroll/special_pay/override/overview'),
    })

  const useSpecialPayShiftOrdersQuery = (req) =>
    useQuery({
      queryKey: ['specialPayShiftOrdersQuery', req],
      queryFn: ({ queryKey }) => api.post('payroll/special_pay_request/shift_orders', queryKey[1]),
      enabled: !!req && !!req?.driver_id && !!req?.shift_date,
    })

  const recalcSinglePayrollMutation = () =>
    useMutation({
      mutationKey: ['recalcSinglePayrollMutation'],
      mutationFn: (request) =>
        api.post('payroll/recalc_payroll', undefined, {
          queryParams: `driver_id=${request.driverId}&date=${request.date}`,
        }),
      onSuccess: async (resp, queryKey) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey: ['payrollOverviewQuery'],
        })

        // Snapshot the previous value
        const previousQueries = queryClient.getQueriesData({
          queryKey: ['payrollOverviewQuery', { date: moment.utc(queryKey.date).format(dateFormat.DATE) }],
        })

        // Optimistically update to the new value
        queryClient.setQueriesData(
          { queryKey: ['payrollOverviewQuery', { date: moment.utc(queryKey.date).format(dateFormat.DATE) }] },
          (oldData) => {
            return {
              ...oldData,
              rows: oldData.rows.map((driverPay) => {
                return queryKey.driverId === driverPay.driver_id ? { ...driverPay, ...resp } : driverPay
              }),
            }
          }
        )
        await queryClient.invalidateQueries({
          queryKey: ['payrollExpandQuery', queryKey.date, queryKey.driverId],
        })
        // Return a context with the previous and new todo
        return { previousQueries, resp }
      },
    })

  const rejectSpecialPayMutation = () =>
    useMutation({
      mutationKey: ['rejectSpecialPayMutation'],
      mutationFn: (request) =>
        api.post('payroll/special_pay_request/reject', null, {
          queryParams: new URLSearchParams({ ...request }),
        }),
    })

  const createSpecialPayMutation = () =>
    useMutation({
      mutationKey: ['rejectSpecialPayMutation'],
      mutationFn: (request) => api.post('payroll/special_pay_request/upsert', request),
    })

  const overrideShiftPayDetailMutation = () =>
    useMutation({
      mutationKey: ['overrideShiftPayDetailMutation'],
      mutationFn: (request) => api.post('payroll/override/shift', request),
      onSuccess: async () => {
        return await queryClient.invalidateQueries({
          queryKey: ['payPeriodDetailsQuery'],
        })
      },
    })

  const overrideOrderPayDetailMutation = () =>
    useMutation({
      mutationKey: ['overrideOrderPayDetailMutation'],
      mutationFn: (request) => api.post('payroll/override/order', request),
      onSuccess: async () => {
        return await queryClient.invalidateQueries({
          queryKey: ['payPeriodDetailsQuery'],
        })
      },
    })

  return {
    usePayrollDatesQuery,
    useSpecialPayRequestsQuery,
    usePayrollOverviewQuery,
    usePayPeriodDetailsQuery,
    usePayrollExpandQuery,
    useSpecialPayTypesQuery,
    useSpecialPayOverridesQuery,
    useSpecialPayShiftOrdersQuery,
    recalcSinglePayrollMutation,
    rejectSpecialPayMutation,
    createSpecialPayMutation,
    overrideShiftPayDetailMutation,
    overrideOrderPayDetailMutation,
  }
}
